import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  MessageCircle, X, Send, Loader2, Bot, User, 
  Minimize2, Maximize2, Trash2, RefreshCw, Check
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const AIChatBubble = () => {
  const { token, API, user, isPlatformAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [tokenExpired, setTokenExpired] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  // Only show for authenticated client users (not platform admin)
  if (!token || !user || isPlatformAdmin()) {
    return null;
  }

  const handleSend = async (confirmAction = null) => {
    if (loading || tokenExpired) return;
    if (!confirmAction && !input.trim()) return;

    const userMessage = confirmAction ? null : input.trim();
    if (!confirmAction) {
      setInput('');
      setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    }
    setLoading(true);

    try {
      const payload = { session_id: sessionId };
      if (confirmAction) {
        payload.confirm_action = confirmAction;
        payload.message = "confirm";
      } else {
        payload.message = userMessage;
      }

      const response = await axios.post(
        `${API}/ai/chat`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSessionId(response.data.session_id);
      
      // Check if there's a pending action
      if (response.data.pending_action) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: response.data.response,
          pendingAction: response.data.pending_action
        }]);
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: response.data.response,
          actionCompleted: response.data.action_completed
        }]);
      }
    } catch (error) {
      console.error('AI Chat error:', error);
      const statusCode = error.response?.status;
      const errorDetail = error.response?.data?.detail || '';
      
      if (statusCode === 401 || errorDetail.toLowerCase().includes('token') || errorDetail.toLowerCase().includes('expired')) {
        setTokenExpired(true);
        setMessages(prev => [...prev, { 
          role: 'expired', 
          content: 'Your session has expired. Please log in again to continue using the AI Assistant.' 
        }]);
      } else {
        const errorMsg = errorDetail || 'Failed to get response. Please try again.';
        setMessages(prev => [...prev, { role: 'error', content: errorMsg }]);
        toast.error('AI Assistant error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAction = (actionId) => {
    handleSend(actionId);
  };

  const handleRelogin = () => {
    logout();
    navigate('/login');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    setIsMinimized(false);
  };

  // Welcome message
  const welcomeMessage = {
    role: 'assistant',
    content: `Hi! I'm your AI assistant. I can help you with questions about your business data like:\n\n• "How many jobs are scheduled today?"\n• "What's our total revenue?"\n• "Show me unpaid invoices"\n• "Which crews are available?"\n\nWhat would you like to know?`
  };

  const displayMessages = messages.length === 0 ? [welcomeMessage] : messages;

  return (
    <>
      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed bottom-24 right-6 z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col transition-all duration-200 ${
            isMinimized ? 'w-80 h-14' : 'w-96 h-[500px]'
          }`}
          data-testid="ai-chat-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-t-2xl">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white">AI Assistant</h3>
                {!isMinimized && (
                  <p className="text-xs text-emerald-100">Ask about your business data</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {!isMinimized && messages.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearChat}
                  className="h-8 w-8 p-0 text-white/80 hover:text-white hover:bg-white/20"
                  title="Clear chat"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMinimized(!isMinimized)}
                className="h-8 w-8 p-0 text-white/80 hover:text-white hover:bg-white/20"
              >
                {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleChat}
                className="h-8 w-8 p-0 text-white/80 hover:text-white hover:bg-white/20"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Messages Area */}
          {!isMinimized && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {displayMessages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.role === 'expired' ? (
                      <div className="max-w-[85%] rounded-2xl px-4 py-3 bg-amber-50 border border-amber-200 rounded-bl-md">
                        <div className="flex items-start gap-2">
                          <RefreshCw className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600" />
                          <div>
                            <p className="text-sm text-amber-800 mb-3">{msg.content}</p>
                            <Button 
                              size="sm" 
                              onClick={handleRelogin}
                              className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
                            >
                              Log In Again
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                          msg.role === 'user'
                            ? 'bg-emerald-600 text-white rounded-br-md'
                            : msg.role === 'error'
                            ? 'bg-red-100 text-red-700 rounded-bl-md'
                            : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {msg.role !== 'user' && (
                            <Bot className={`h-4 w-4 mt-0.5 flex-shrink-0 ${msg.role === 'error' ? 'text-red-500' : 'text-emerald-600'}`} />
                          )}
                          <div className="flex-1">
                            <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                            
                            {/* Action Confirmation Buttons */}
                            {msg.pendingAction && (
                              <div className="mt-3 pt-3 border-t border-gray-100">
                                <p className="text-xs text-gray-500 mb-2">
                                  Action: {msg.pendingAction.description}
                                </p>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => handleConfirmAction(msg.pendingAction.action_id)}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7"
                                    disabled={loading}
                                  >
                                    Yes, do it
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setMessages(prev => prev.map((m, i) => 
                                      i === idx ? {...m, pendingAction: null, content: m.content + "\n\n(Action cancelled)"} : m
                                    ))}
                                    className="text-xs h-7"
                                    disabled={loading}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            )}
                            
                            {/* Action Completed Badge */}
                            {msg.actionCompleted && (
                              <div className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded">
                                <Check className="h-3 w-3" />
                                Action completed
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-white text-gray-800 shadow-sm border border-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Bot className="h-4 w-4 text-emerald-600" />
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-emerald-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 bg-emerald-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-2 h-2 bg-emerald-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="p-3 border-t border-gray-100 bg-white rounded-b-2xl">
                {tokenExpired ? (
                  <div className="text-center py-2">
                    <p className="text-sm text-gray-500">Session expired. Please log in again.</p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask about jobs, invoices, customers..."
                      className="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      disabled={loading}
                      data-testid="ai-chat-input"
                    />
                    <Button
                      onClick={() => handleSend()}
                      disabled={loading || !input.trim()}
                      className="h-10 w-10 p-0 rounded-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="ai-chat-send"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={toggleChat}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105 ${
          isOpen 
            ? 'bg-gray-700 hover:bg-gray-800' 
            : 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800'
        }`}
        data-testid="ai-chat-bubble"
      >
        {isOpen ? (
          <X className="h-6 w-6 text-white" />
        ) : (
          <MessageCircle className="h-6 w-6 text-white" />
        )}
      </button>
    </>
  );
};

export default AIChatBubble;
